import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild, QueryList, ViewChildren, Output, EventEmitter, Input, OnChanges, SimpleChanges } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf'; 
import { PDFDocumentProxy } from 'pdfjs-dist/legacy/build/pdf';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { DocumentFormService } from 'src/app/app-core/services/document-form.service';
import { ISignaturePlaceholder, ISignaturePosition } from 'src/app/interfaces/signature-gov.interfaces';
import { environment } from 'src/environments/environment';

pdfjsLib.GlobalWorkerOptions.workerSrc = environment.pdfWorker;

@Component({
  selector: 'placeholder-sign',
  templateUrl: './placeholder-sign.component.html',
  styleUrls: ['./placeholder-sign.component.scss'],
  providers: [DocumentFormService]
})
export class PlaceholderSignComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @ViewChild('container', { static: false }) container: ElementRef;
  @ViewChild('draggable', { static: false }) draggable: ElementRef;
  @ViewChildren('pdfCanvas') pdfCanvases: QueryList<ElementRef>;

  @Input() pdfBuffer: ArrayBuffer;
  @Input() base64DocumentSign: string;
  @Input() wasSign = false;
  @Input() isLoadingSign = false;
  /** Onde o marcador começa; sem ela, fica no canto do documento como sempre ficou. */
  @Input() suggestedPosition: ISignaturePosition | null = null;

  @Output() signaturePosition = new EventEmitter<ISignaturePlaceholder>();  

  public pdfPages: any[] = []; 
  public pdfPagesNum = [];
  public pdfUrl: SafeResourceUrl;
  public position = { x: 50, y: 50 }; 
  public scaleFactor = 1.4; 
  /** Página exibida / página desenhada: o marcador acompanha a página quando ela encolhe. */
  public markerScale = 1;
  private isDragging = false;
  private dragOffset = { x: 0, y: 0 };
  private resizeSubscription: Subscription;
  
  constructor(
    private documentService: DocumentFormService,
    private cd: ChangeDetectorRef,
  ) { 
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.base64DocumentSign && this.base64DocumentSign) {
      this.pdfBuffer = this.convertBase64ToArrayBuffer(this.base64DocumentSign);
      this.generateDocumentAndRenderPages();
    }
  }

  ngOnInit(): void {
    this.generateDocumentAndRenderPages().then(() => this.applySuggestedPosition());
    this.emitSignatureOutOfScopeEvent();
  }

  /**
   * Leva o marcador até a posição sugerida, com as páginas já desenhadas, e a informa
   * como posição de assinatura: o cidadão pode assinar direto ou ainda arrastá-lo.
   * A posição segue o SignService: x no centro do carimbo, y no topo, ambos em % da página.
   */
  private applySuggestedPosition(): void {
    const sugestao = this.suggestedPosition;
    const canvas = sugestao ? this.pdfCanvases?.toArray()[sugestao.page]?.nativeElement : null;
    const container = this.container?.nativeElement;
    const draggable = this.draggable?.nativeElement;

    if (!sugestao || this.wasSign || !canvas || !container || !draggable) return;

    const canvasRect = canvas.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const draggableRect = draggable.getBoundingClientRect();

    if (!canvasRect.width || !canvasRect.height) return;

    const centroX = canvasRect.left - containerRect.left + container.scrollLeft + (canvasRect.width * sugestao.xPercentage) / 100;
    const centroY = canvasRect.top - containerRect.top + container.scrollTop + canvasRect.height * (1 - sugestao.yPercentage / 100);

    this.position = {
      x: centroX - draggableRect.width / 2,
      y: centroY - draggableRect.height / 2,
    };

    container.scrollTop = Math.max(0, centroY - container.clientHeight / 2);

    this.signaturePosition.emit({
      outOfScopePdf: false,
      signaturePosition: { ...sugestao },
    });
  }

  ngAfterViewInit() {
    // A página já se ajusta à largura pelo CSS: no resize (girar o celular, a barra do
    // navegador sumindo) só o marcador precisa acompanhar o novo tamanho dela.
    this.resizeSubscription = fromEvent(window, 'resize')
                                      .pipe(debounceTime(200)) 
                                      .subscribe(() => this.updateMarkerScale());

    this.setInitialPosition();
  }

  ngOnDestroy(): void {
    this.resizeSubscription?.unsubscribe();
  }

  setInitialPosition(): void {
    const firstCanvas = this.pdfCanvases.first?.nativeElement;
  
    if (firstCanvas) {
      const rect = firstCanvas.getBoundingClientRect();
      this.position = {
        x: 50, 
        y: 50,
      };

    }
  }


  adjustDraggablePlaceholder(): void {
    const firstCanvas = this.pdfCanvases.first?.nativeElement;
  
    if (!firstCanvas) {
      return;
    }
  
    const rect = firstCanvas.getBoundingClientRect();
  
    const scale = this.scaleFactor;
  
    const placeholderWidth = 200 * scale; 
    const placeholderHeight = 80 * scale; 
  
    const placeholder = this.draggable.nativeElement;
    if (placeholder) {
      placeholder.style.width = `${placeholderWidth}px`;
      placeholder.style.height = `${placeholderHeight}px`;
  
      const newPositionX = rect.left + (this.position.x * scale);
      const newPositionY = rect.top + (this.position.y * scale);
  
      placeholder.style.left = `${newPositionX}px`;
      placeholder.style.top = `${newPositionY}px`;
    }
  }

  async generateDocumentAndRenderPages() {
    const clonedBuffer = new Uint8Array(this.pdfBuffer.slice(0)); 
    const pdf = await pdfjsLib.getDocument(clonedBuffer).promise;
    
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      // Ao redesenhar (girar o celular, documento assinado), reaproveita os canvas que já existem.
      if (this.pdfPagesNum.length < pageNum) this.pdfPagesNum.push(true);
      await this.renderPage(pageNum, pdf);
    }

    this.updateMarkerScale();
  }

  /**
   * O marcador tem o tamanho do carimbo na página desenhada. No celular a página encolhe
   * até caber na tela, e o marcador encolhe junto: maior que a página, ele nunca ficaria
   * inteiro dentro dela e a assinatura não seria liberada.
   */
  private updateMarkerScale(): void {
    const canvas: HTMLCanvasElement = this.pdfCanvases?.first?.nativeElement;
    const larguraExibida = canvas?.getBoundingClientRect().width;

    this.markerScale = canvas?.width && larguraExibida ? Math.min(1, larguraExibida / canvas.width) : 1;
    this.cd.detectChanges();
  }

  async renderPage(pageNum: number, pdf?: PDFDocumentProxy) {
    try {
      const page = await pdf.getPage(pageNum);
      
      const canvas = this.pdfCanvases.toArray()[pageNum - 1]?.nativeElement;
      const context = canvas?.getContext('2d');
      
      if (!canvas || !context) {
        return;
      }
      
      const viewport = page.getViewport({ scale: this.scaleFactor});
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({
        canvasContext: context,
        viewport: viewport,
      }).promise;
      
      console.log(`Página ${pageNum} renderizada com sucesso.`);
    } catch (err) {
      console.error("Erro ao renderizar a página: ", err);
    }
  }

  handleClick(event: MouseEvent): void {
    this.moveDraggable(event);
    setTimeout(() => this.checkScopeAndEmitEventOfPercent(event, false), 100);
  }

  startDragging(event: MouseEvent) {
    this.isDragging = true;
    this.dragOffset = {
      x: event.clientX - this.position.x,
      y: event.clientY - this.position.y,
    };
  }

  onMouseMove(event: MouseEvent) {
    if (this.isDragging) {
      this.moveDraggable(event);
    }
  }

  onMouseUp(event: MouseEvent) {
    this.checkScopeAndEmitEventOfPercent(event)
  }

  checkScopeAndEmitEventOfPercent(event: MouseEvent, haveDraggable = true) {
    const canvasElements = this.pdfCanvases.toArray();
    const draggableRect = this.draggable.nativeElement.getBoundingClientRect();
    const draggable = haveDraggable? this.isDragging : true;

    for (let i = 0; i < canvasElements.length; i++) {
      const canvas = canvasElements[i].nativeElement;
      const rect = canvas.getBoundingClientRect();
      
      const x = (draggableRect.left - rect.left) + (draggableRect.width / 2); 
      const y = (draggableRect.top- rect.top) + (draggableRect.height / 2); 

      const canvasWidth = rect.width;
      const canvasHeight = rect.height;

      if (
        draggable &&
        draggableRect.left  >= rect.left &&
        draggableRect.right <= rect.right &&
        draggableRect.top >= rect.top &&
        draggableRect.bottom <= rect.bottom
      ) {
              
        const xPercentage = ((x  / canvasWidth) * 100);
        const yPercentage = Math.abs(((y / canvasHeight) * 100) - 100);  
  
        this.signaturePosition.emit({
          outOfScopePdf: false,
          signaturePosition: {
            page: i,
            xPercentage,
            yPercentage
          }
        });

        break;
      }
      else {
        this.emitSignatureOutOfScopeEvent();
      }
    }

    this.moveDraggable(event);

    if (haveDraggable)  this.isDragging = false; 
    
  }

  emitSignatureOutOfScopeEvent(): void {
    this.signaturePosition.emit({
      outOfScopePdf: true,
      signaturePosition: {
        page: 0,
        xPercentage: 0,
        yPercentage: 0,
      },
    });
  }

  moveDraggable(event: MouseEvent) {
    const draggableRect = this.draggable.nativeElement.getBoundingClientRect();
    const containerRect = this.container.nativeElement.getBoundingClientRect();

    this.position = {
      x: (event.clientX - containerRect.left) + this.container.nativeElement.scrollLeft - draggableRect.width / 2,
      y: (event.clientY - containerRect.top) + this.container.nativeElement.scrollTop - draggableRect.height / 2,
    };
  }

  convertBase64ToArrayBuffer(base64: string): ArrayBuffer {
  
    const binaryString = atob(base64);
    const length = binaryString.length;
    const bytes = new Uint8Array(length);

    for (let i = 0; i < length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    return bytes.buffer;
  }
}
